import {useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import type {FirstPaidUpgradeChange, SubscriptionUpgradePreview} from "@calendall/shared";

/** True when saving the subscription changes anything staff must confirm. */
export function hasPreviewChanges(preview: SubscriptionUpgradePreview | undefined): preview is SubscriptionUpgradePreview {
    return Boolean(preview && (preview.upgradeDefaultsChanges.length > 0 || preview.onlineBookingChange || preview.removesDemoSubscription));
}

function formatValue(value: number | boolean | string | null | undefined, t: (key: string) => string): string {
    if (value === undefined || value === null) return t("PAID_UPGRADE_NOT_SET");
    if (typeof value === "boolean") return value ? t("PAID_UPGRADE_ON") : t("PAID_UPGRADE_OFF");
    return typeof value === "number" ? value.toLocaleString() : value;
}

function changeRow(change: FirstPaidUpgradeChange, t: (key: string) => string): { label: string, from: string, to: string } {
    switch (change.kind) {
        case "QUOTA":
            return change.quota === "email"
                    ? {label: t("PAID_UPGRADE_EMAIL_TOTAL"), from: formatValue(change.currentValue, t), to: formatValue(change.newValue, t)}
                    : {
                        label: t("PAID_UPGRADE_STORAGE_TOTAL_MB"),
                        from: formatValue(Math.round(change.currentValue / (1024 * 1024)), t),
                        to: formatValue(Math.round(change.newValue / (1024 * 1024)), t),
                    };
        case "BOOKING_SETTING":
            return {label: t(`PAID_UPGRADE_SETTING_${change.setting}`), from: formatValue(change.currentValue, t), to: formatValue(change.newValue, t)};
        case "SMS_PROVIDER":
            return {label: t("PAID_UPGRADE_SMS_PROVIDER"), from: formatValue(change.currentValue, t), to: formatValue(change.newValue, t)};
    }
}

export function UpgradePreviewDialog({preview, outdated, saving, onConfirm, onCancel}: {
    preview: SubscriptionUpgradePreview;
    outdated: boolean;
    saving: boolean;
    onConfirm: (skipUpgradeDefaults: boolean) => void;
    onCancel: () => void;
}) {
    const {t} = useTranslation();
    const [skipUpgradeDefaults, setSkipUpgradeDefaults] = useState(false);
    const rows = [
        ...(skipUpgradeDefaults ? [] : preview.upgradeDefaultsChanges.map((change) => changeRow(change, t))),
        ...(preview.onlineBookingChange ? [{
            label: t("PAID_UPGRADE_ONLINE_BOOKING"),
            from: formatValue(preview.onlineBookingChange.currentValue, t),
            to: formatValue(preview.onlineBookingChange.newValue, t),
        }] : []),
    ];

    return (
            <Dialog open onClose={onCancel} maxWidth="sm" fullWidth aria-labelledby="upgrade-preview-title">
                <DialogTitle id="upgrade-preview-title">{t("PAID_UPGRADE_PREVIEW_TITLE")}</DialogTitle>
                <DialogContent>
                    {outdated && <Alert severity="warning" sx={{mb: 2}}>{t("PAID_UPGRADE_PREVIEW_OUTDATED")}</Alert>}
                    <DialogContentText>{t("PAID_UPGRADE_PREVIEW_INTRO")}</DialogContentText>
                    <Box component="ul" sx={{mt: 1.5, mb: 0, pl: 2.5}}>
                        {rows.map((row) => (
                                <li key={row.label}>{t("PAID_UPGRADE_PREVIEW_ROW", row)}</li>
                        ))}
                        {preview.removesDemoSubscription && <li>{t("PAID_UPGRADE_PREVIEW_REMOVES_DEMO")}</li>}
                    </Box>
                    {preview.upgradeDefaultsChanges.length > 0 && (
                            <FormControlLabel
                                    sx={{mt: 1.5}}
                                    control={<Checkbox checked={skipUpgradeDefaults} onChange={(e) => setSkipUpgradeDefaults(e.target.checked)} />}
                                    label={t("PAID_UPGRADE_SKIP_DEFAULTS")}
                            />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onCancel}>{t("CANCEL")}</Button>
                    <Button variant="contained" onClick={() => onConfirm(skipUpgradeDefaults)} disabled={saving}>
                        {saving ? t("DETAIL_SAVING") : t("PAID_UPGRADE_CONFIRM")}
                    </Button>
                </DialogActions>
            </Dialog>
    );
}
